import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Trash2, Plus, GripVertical, ToggleLeft, ToggleRight, Pencil, Check, X } from 'lucide-react';
import { AIAgentList, AIAgentListItem, updateAgentList, deleteAgentList } from '../../lib/aiAgents';
import toast from 'react-hot-toast';

interface AgentListCardProps {
  list: AIAgentList;
  isNew?: boolean;
  onUpdated: () => void;
  onDeleted: () => void;
}

export default function AgentListCard({ list, isNew, onUpdated, onDeleted }: AgentListCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState(list.title);
  const [items, setItems] = useState<AIAgentListItem[]>(list.items || []);
  const [newLabel, setNewLabel] = useState('');
  const [newValue, setNewValue] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  const saveItems = async (updatedItems: AIAgentListItem[]) => {
    setSaving(true);
    try {
      await updateAgentList(list.id, { items: updatedItems });
      setItems(updatedItems);
      onUpdated();
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTitle = async () => {
    if (!titleValue.trim()) return;
    try {
      await updateAgentList(list.id, { title: titleValue.trim() });
      setEditingTitle(false);
      onUpdated();
    } catch {
      toast.error('Failed to rename');
    }
  };

  const handleAddItem = async () => {
    if (!newLabel.trim()) return;
    const updated = [...items, { label: newLabel.trim(), value: newValue.trim() }];
    await saveItems(updated);
    setNewLabel('');
    setNewValue('');
  };

  const handleRemoveItem = async (index: number) => {
    const updated = items.filter((_, i) => i !== index);
    await saveItems(updated);
  };

  const handleStartEdit = (index: number) => {
    setEditingIndex(index);
    setEditLabel(items[index].label);
    setEditValue(items[index].value);
  };

  const handleSaveEdit = async () => {
    if (editingIndex === null || !editLabel.trim()) return;
    const updated = [...items];
    updated[editingIndex] = { label: editLabel.trim(), value: editValue.trim() };
    await saveItems(updated);
    setEditingIndex(null);
  };

  const handleToggleActive = async () => {
    try {
      await updateAgentList(list.id, { is_active: !list.is_active });
      onUpdated();
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Delete list "${list.title}"?`)) return;
    try {
      await deleteAgentList(list.id);
      toast.success('List deleted');
      onDeleted();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddItem();
    }
  };

  return (
    <div
      className={`rounded-lg border transition-all duration-200 ${
        isNew ? 'border-purple-300 bg-purple-50/50 animate-pulse-once' : ''
      } ${
        list.is_active
          ? 'border-gray-200 bg-white hover:border-gray-300'
          : 'border-gray-100 bg-gray-50 opacity-60'
      }`}
    >
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-3 py-2.5 flex items-center gap-2 text-left"
      >
        {expanded ? (
          <ChevronDown className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <span className={`text-sm font-medium truncate ${list.is_active ? 'text-gray-900' : 'text-gray-500'}`}>
            {list.title}
          </span>
          <span className="text-xs text-gray-400 ml-2">
            {items.length} item{items.length !== 1 ? 's' : ''}
          </span>
        </div>
      </button>

      {/* Expanded Content */}
      {expanded && (
        <div className="px-3 pb-3 border-t border-gray-100">
          {/* Title Edit */}
          <div className="mt-2 mb-2">
            {editingTitle ? (
              <div className="flex items-center gap-1.5">
                <input
                  type="text"
                  value={titleValue}
                  onChange={(e) => setTitleValue(e.target.value)}
                  className="flex-1 text-xs border border-gray-300 rounded px-2 py-1 focus:ring-1 focus:ring-blue-500 outline-none"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveTitle();
                    if (e.key === 'Escape') { setEditingTitle(false); setTitleValue(list.title); }
                  }}
                />
                <button onClick={handleSaveTitle} className="p-0.5 hover:bg-gray-100 rounded">
                  <Check className="h-3 w-3 text-green-600" />
                </button>
                <button onClick={() => { setEditingTitle(false); setTitleValue(list.title); }} className="p-0.5 hover:bg-gray-100 rounded">
                  <X className="h-3 w-3 text-gray-400" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setEditingTitle(true)}
                className="text-[10px] text-blue-500 hover:text-blue-600 flex items-center gap-0.5"
              >
                <Pencil className="h-2.5 w-2.5" /> Rename
              </button>
            )}
          </div>

          {/* Items */}
          <div className="space-y-1">
            {items.map((item, idx) => (
              <div key={idx} className="group">
                {editingIndex === idx ? (
                  <div className="flex gap-1 items-center bg-blue-50 rounded px-2 py-1.5">
                    <input
                      type="text"
                      value={editLabel}
                      onChange={(e) => setEditLabel(e.target.value)}
                      placeholder="Label"
                      className="flex-1 text-xs border border-gray-300 rounded px-1.5 py-1 outline-none focus:ring-1 focus:ring-blue-500 min-w-0"
                      autoFocus
                    />
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      placeholder="Value"
                      className="flex-1 text-xs border border-gray-300 rounded px-1.5 py-1 outline-none focus:ring-1 focus:ring-blue-500 min-w-0"
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSaveEdit(); }}
                    />
                    <button onClick={handleSaveEdit} className="p-0.5 hover:bg-blue-100 rounded flex-shrink-0">
                      <Check className="h-3 w-3 text-green-600" />
                    </button>
                    <button onClick={() => setEditingIndex(null)} className="p-0.5 hover:bg-blue-100 rounded flex-shrink-0">
                      <X className="h-3 w-3 text-gray-400" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 px-2 py-1.5 rounded hover:bg-gray-50 transition-colors">
                    <GripVertical className="h-3 w-3 text-gray-300 flex-shrink-0" />
                    <span className="text-xs text-gray-700 font-medium truncate">{item.label}</span>
                    {item.value && (
                      <>
                        <span className="text-xs text-gray-300">—</span>
                        <span className="text-xs text-gray-500 truncate">{item.value}</span>
                      </>
                    )}
                    <div className="ml-auto flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                      <button onClick={() => handleStartEdit(idx)} className="p-0.5 hover:bg-gray-200 rounded">
                        <Pencil className="h-2.5 w-2.5 text-gray-400" />
                      </button>
                      <button onClick={() => handleRemoveItem(idx)} className="p-0.5 hover:bg-gray-200 rounded">
                        <Trash2 className="h-2.5 w-2.5 text-red-400" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Add Item */}
          <div className="flex gap-1 mt-2 items-center">
            <input
              type="text"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="Label (e.g. Junk Rate)"
              className="flex-1 text-xs border border-gray-300 rounded px-2 py-1.5 outline-none focus:ring-1 focus:ring-blue-500 min-w-0"
              onKeyDown={handleKeyDown}
              disabled={saving}
            />
            <input
              type="text"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              placeholder="Value (e.g. $0.12/lb)"
              className="flex-1 text-xs border border-gray-300 rounded px-2 py-1.5 outline-none focus:ring-1 focus:ring-blue-500 min-w-0"
              onKeyDown={handleKeyDown}
              disabled={saving}
            />
            <button
              onClick={handleAddItem}
              disabled={!newLabel.trim() || saving}
              className="p-1.5 bg-gray-100 hover:bg-gray-200 rounded transition-colors disabled:opacity-50 flex-shrink-0"
            >
              <Plus className="h-3 w-3 text-gray-600" />
            </button>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 mt-3 pt-2 border-t border-gray-100">
            <button
              onClick={handleToggleActive}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
            >
              {list.is_active ? <ToggleRight className="h-3 w-3" /> : <ToggleLeft className="h-3 w-3" />}
              {list.is_active ? 'Active' : 'Inactive'}
            </button>
            <button
              onClick={handleDelete}
              className="flex items-center gap-1 text-xs text-red-500 hover:text-red-600 transition-colors ml-auto"
            >
              <Trash2 className="h-3 w-3" />
              Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
